<script setup lang="ts">
import type { StudyCoachAction, StudyCoachContext, StudyCoachEvent, StudyCoachSuggestion } from '@/features/study-coach/studyCoachAgent';
import { useLocalStorage } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import MarkdownDiv from '@/components/markdown-div/MarkdownDiv.vue';
import {
  buildFallbackSuggestion,
  filterStudyCoachWrongbook,
  getStudyCoachSuggestion,
  getStudyCoachWrongbookRetryAt,
  invalidateStudyCoachWrongbook,
  loadStudyCoachWrongbook,
} from '@/features/study-coach/studyCoachAgent';
import { useConfigStore, useUserStore } from '@/store';

const router = useRouter();
const configStore = useConfigStore();
const userStore = useUserStore();
const wrongbook = ref<any[]>([]);
const suggestion = ref<StudyCoachSuggestion | null>(null);
const open = ref(false);
const loading = ref(false);
const failed = ref(false);
const retryAt = ref(0);
const subject = computed(() => configStore.currentSubject);
const subjectId = computed(() => String((subject.value as any)?.id_ ?? configStore.currentSubjectId ?? ''));
const subjectName = computed(() => String((subject.value as any)?.name ?? (subject.value as any)?.subjectName ?? '当前学科'));
const username = computed(() => String(userStore.user?.username ?? userStore.user?.id ?? '').trim());
const sortedWrongbook = computed(() => [...wrongbook.value].sort((a, b) => (b.errorCount ?? 0) - (a.errorCount ?? 0)));
const retryBlocked = computed(() => retryAt.value > Date.now());
const dragOffset = useLocalStorage('study-coach-mascot-offset', { x: 0, y: 0 });
const isDragging = ref(false);
let dragMoved = false;
let dragStart: { x: number; y: number; offsetX: number; offsetY: number } | null = null;
let requestToken = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

function makeContext(event: StudyCoachEvent): StudyCoachContext {
  return {
    subjectId: subjectId.value,
    subjectName: subjectName.value,
    wrongbook: wrongbook.value,
    event,
  };
}

function setRetryAt(value: number | undefined) {
  retryAt.value = value ?? 0;
  if (retryTimer)
    clearTimeout(retryTimer);
  if (retryAt.value > Date.now()) {
    retryTimer = setTimeout(() => {
      retryAt.value = 0;
    }, retryAt.value - Date.now());
  }
}

async function refresh(event: StudyCoachEvent) {
  if (loading.value)
    return;
  if (!username.value || !subject.value) {
    suggestion.value = buildFallbackSuggestion(makeContext(event));
    failed.value = true;
    return;
  }

  const token = ++requestToken;
  const requestedSubjectId = subjectId.value;
  const requestedUsername = username.value;
  loading.value = true;
  failed.value = false;
  setRetryAt(undefined);
  try {
    const items = await loadStudyCoachWrongbook(requestedUsername);
    if (token !== requestToken || requestedSubjectId !== subjectId.value || requestedUsername !== username.value)
      return;
    wrongbook.value = filterStudyCoachWrongbook(items, { subjectId: requestedSubjectId, subjectName: subjectName.value });
    const result = await getStudyCoachSuggestion(makeContext(event), requestedUsername);
    if (token !== requestToken || requestedSubjectId !== subjectId.value || requestedUsername !== username.value)
      return;
    suggestion.value = result.suggestion;
    failed.value = result.source === 'fallback' && result.retryAt !== undefined;
    setRetryAt(result.retryAt);
  }
  catch (error) {
    console.warn('[study-coach] failed to load', error);
    if (token !== requestToken || requestedSubjectId !== subjectId.value)
      return;
    suggestion.value = buildFallbackSuggestion(makeContext(event));
    failed.value = true;
    setRetryAt(getStudyCoachWrongbookRetryAt(requestedUsername));
  }
  finally {
    if (token === requestToken)
      loading.value = false;
  }
}

function handleAction(action: StudyCoachAction) {
  if (action.type === 'review_question' && action.questionId) {
    void router.push({ path: '/mistakerecord', query: { questionId: String(action.questionId) } });
    open.value = false;
  }
  else if (action.type === 'open_wrongbook') {
    void router.push('/mistakerecord');
    open.value = false;
  }
  else if (action.type === 'show_more' || action.type === 'chat') {
    open.value = true;
  }
  else if (action.type === 'dismiss') {
    open.value = false;
  }
}

function retrySuggestion() {
  if (!loading.value && !retryBlocked.value)
    void refresh('user_requested_help');
}

function onReviewCompleted(event: Event) {
  const detail = (event as CustomEvent<{ questionId?: number }>).detail;
  invalidateStudyCoachWrongbook(username.value, detail?.questionId);
  if (detail?.questionId)
    wrongbook.value = wrongbook.value.filter(item => item.questionId !== detail.questionId);
  suggestion.value = buildFallbackSuggestion(makeContext('review_completed'));
  failed.value = false;
  setRetryAt(undefined);
  requestToken++;
  loading.value = false;
}

function toggleOpen() {
  if (open.value) {
    open.value = false;
    return;
  }
  open.value = true;
  if (!loading.value)
    void refresh('user_requested_help');
}

function onPointerMove(event: PointerEvent) {
  if (!dragStart)
    return;
  const margin = 12;
  const mascotSize = 96;
  const x = dragStart.offsetX + event.clientX - dragStart.x;
  const y = dragStart.offsetY + event.clientY - dragStart.y;
  dragMoved ||= Math.abs(event.clientX - dragStart.x) > 5 || Math.abs(event.clientY - dragStart.y) > 5;
  dragOffset.value = {
    x: Math.max(-window.innerWidth + mascotSize + margin, Math.min(margin, x)),
    y: Math.max(-window.innerHeight + mascotSize + margin, Math.min(margin, y)),
  };
}

function stopDragging() {
  if (!dragStart)
    return;
  dragStart = null;
  isDragging.value = false;
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', stopDragging);
  window.removeEventListener('pointercancel', stopDragging);
}

function startDragging(event: PointerEvent) {
  if (event.button !== 0)
    return;
  event.preventDefault();
  event.stopPropagation();
  (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
  dragStart = { x: event.clientX, y: event.clientY, offsetX: dragOffset.value.x, offsetY: dragOffset.value.y };
  dragMoved = false;
  isDragging.value = true;
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopDragging);
  window.addEventListener('pointercancel', stopDragging);
}

function onMascotClick() {
  if (dragMoved) {
    dragMoved = false;
    return;
  }
  toggleOpen();
}

watch([subjectId, username], ([nextSubject, nextUsername], [previousSubject, previousUsername]) => {
  if (nextSubject === previousSubject && nextUsername === previousUsername)
    return;
  requestToken++;
  wrongbook.value = [];
  suggestion.value = null;
  open.value = false;
  loading.value = false;
  failed.value = false;
  setRetryAt(undefined);
});

onMounted(() => window.addEventListener('probfun:wrongbook-review-completed', onReviewCompleted));

onBeforeUnmount(() => {
  requestToken++;
  window.removeEventListener('probfun:wrongbook-review-completed', onReviewCompleted);
  stopDragging();
  if (retryTimer)
    clearTimeout(retryTimer);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="subject" class="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col items-end sm:bottom-6 sm:right-6" :style="{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }">
      <div v-if="open" class="pointer-events-auto mb-3 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border bg-card shadow-xl">
        <div class="flex items-center justify-between border-b px-4 py-3">
          <div>
            <div class="text-sm font-semibold">
              {{ subjectName }}复习教练
            </div>
            <div class="text-xs text-muted-foreground">
              {{ wrongbook.length }} 道错题
            </div>
          </div>
          <button class="text-muted-foreground hover:text-foreground" aria-label="关闭" @click="open = false">
            ×
          </button>
        </div>
        <div class="max-h-72 overflow-y-auto p-3">
          <p v-if="loading" class="p-3 text-sm text-muted-foreground">
            正在整理你的错题...
          </p>
          <template v-else>
            <div v-if="suggestion" class="mb-3 text-sm leading-6 [&_.prose]:text-sm [&_.prose]:leading-6 [&_p]:my-1">
              <MarkdownDiv :text="suggestion.message" />
            </div>
            <p v-else-if="failed" class="mb-3 text-sm leading-6 text-muted-foreground">
              暂时无法读取错题，请稍后重试。
            </p>
            <button
              v-if="failed"
              class="mb-3 rounded-md bg-primary px-3 py-2 text-xs text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="retryBlocked"
              @click="retrySuggestion"
            >
              {{ retryBlocked ? '稍后重试' : (suggestion ? '重新生成建议' : '重新加载错题') }}
            </button>
            <div
              v-for="item in sortedWrongbook.slice(0, 5)"
              :key="item.questionId"
              class="mb-2 w-full cursor-pointer rounded-lg border p-2 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              role="button"
              tabindex="0"
              @click="handleAction({ type: 'review_question', label: '复习', questionId: item.questionId })"
              @keydown.enter="handleAction({ type: 'review_question', label: '复习', questionId: item.questionId })"
              @keydown.space.prevent="handleAction({ type: 'review_question', label: '复习', questionId: item.questionId })"
            >
              <span class="font-medium">错误 {{ item.errorCount }} 次</span>
              <span class="ml-2 text-muted-foreground">{{ item.chapterName || '未分类' }}</span>
              <MarkdownDiv class="mt-1 line-clamp-2 text-muted-foreground [&_.prose]:text-xs [&_.prose]:leading-5 [&_p]:my-0" :text="item.questionContent" />
            </div>
          </template>
        </div>
        <div class="flex gap-2 border-t p-3">
          <button v-for="action in (suggestion?.actions ?? [])" :key="`${action.type}-${action.questionId ?? ''}`" class="rounded-md bg-primary px-3 py-2 text-xs text-primary-foreground" @click="handleAction(action)">
            {{ action.label }}
          </button>
        </div>
      </div>
      <button
        class="pointer-events-auto size-20 touch-none select-none overflow-hidden rounded-full border-2 border-amber-300 bg-slate-950 shadow-lg transition-transform hover:scale-105 sm:size-24"
        :aria-label="open ? '关闭错题复习教练' : '打开错题复习教练'"
        :aria-expanded="open"
        :class="{ 'cursor-grabbing': isDragging, 'cursor-grab': !isDragging }"
        @pointerdown="startDragging"
        @click="onMascotClick"
      >
        <img src="/newton-coach.png" alt="" draggable="false" class="pointer-events-none h-full w-full select-none object-cover">
      </button>
    </div>
  </Teleport>
</template>
